import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OnboardingPreferences } from './types'

const CUISINE_OPTIONS = ['British', 'Italian', 'Asian', 'Mexican', 'Mediterranean', 'Other']
const DIETARY_OPTIONS = ['None', 'Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free']

const MIN_HOUSEHOLD_SIZE = 1
const MAX_HOUSEHOLD_SIZE = 10
const DEFAULT_HOUSEHOLD_SIZE = 2

interface Props {
  onContinue: (preferences: OnboardingPreferences) => void
  onSkip: () => void
}

export function OnboardingPreferencesStep({ onContinue, onSkip }: Props) {
  const { t } = useTranslation('onboarding')
  const [householdSize, setHouseholdSize] = useState(DEFAULT_HOUSEHOLD_SIZE)
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([])
  const [selectedDietary, setSelectedDietary] = useState<string[]>([])

  const toggleCuisine = (cuisine: string) => {
    setSelectedCuisines((prev) =>
      prev.includes(cuisine) ? prev.filter((c) => c !== cuisine) : [...prev, cuisine],
    )
  }

  const toggleDietary = (constraint: string) => {
    if (constraint === 'None') {
      setSelectedDietary((prev) => (prev.includes('None') ? [] : ['None']))
      return
    }
    setSelectedDietary((prev) => {
      const without = prev.filter((d) => d !== 'None' && d !== constraint)
      return prev.includes(constraint) ? without : [...without, constraint]
    })
  }

  const handleContinue = () => {
    onContinue({
      householdSize,
      cuisines: selectedCuisines,
      dietaryConstraints: selectedDietary,
    })
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-ink">{t('preferences.heading')}</h1>
      </div>

      {/* Household size stepper */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink mb-2">
          {t('preferences.householdSize')}
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label={t('preferences.decrementAriaLabel')}
            disabled={householdSize <= MIN_HOUSEHOLD_SIZE}
            onClick={() => setHouseholdSize((s) => Math.max(MIN_HOUSEHOLD_SIZE, s - 1))}
            className="h-10 w-10 rounded-full border border-line bg-surface text-ink font-bold text-lg hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            -
          </button>
          <span
            className="text-2xl font-bold text-ink w-8 text-center"
            aria-label={t('preferences.householdSizeAriaLabel', { count: householdSize })}
            aria-live="polite"
          >
            {householdSize}
          </span>
          <button
            type="button"
            aria-label={t('preferences.incrementAriaLabel')}
            disabled={householdSize >= MAX_HOUSEHOLD_SIZE}
            onClick={() => setHouseholdSize((s) => Math.min(MAX_HOUSEHOLD_SIZE, s + 1))}
            className="h-10 w-10 rounded-full border border-line bg-surface text-ink font-bold text-lg hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            +
          </button>
        </div>
      </div>

      {/* Cuisine preferences */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink mb-2">
          {t('preferences.cuisines')}
        </label>
        <div className="flex flex-wrap gap-2">
          {CUISINE_OPTIONS.map((cuisine) => (
            <button
              key={cuisine}
              type="button"
              role="checkbox"
              aria-checked={selectedCuisines.includes(cuisine)}
              onClick={() => toggleCuisine(cuisine)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition focus:outline-none focus:ring-2 focus:ring-accent ${
                selectedCuisines.includes(cuisine)
                  ? 'bg-accent text-white border-accent'
                  : 'bg-surface text-ink border-line hover:border-accent/50'
              }`}
            >
              {cuisine}
            </button>
          ))}
        </div>
      </div>

      {/* Dietary constraints */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink mb-2">
          {t('preferences.dietary')}
        </label>
        <div className="flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map((constraint) => (
            <button
              key={constraint}
              type="button"
              role="checkbox"
              aria-checked={selectedDietary.includes(constraint)}
              onClick={() => toggleDietary(constraint)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition focus:outline-none focus:ring-2 focus:ring-accent ${
                selectedDietary.includes(constraint)
                  ? 'bg-accent text-white border-accent'
                  : 'bg-surface text-ink border-line hover:border-accent/50'
              }`}
            >
              {constraint}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-auto pt-4 pb-2 flex flex-col gap-2">
        <button
          type="button"
          onClick={handleContinue}
          className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition"
        >
          {t('preferences.continue')}
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="w-full rounded-lg bg-transparent text-muted font-medium py-2 px-4 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent transition"
        >
          {t('preferences.skip')}
        </button>
      </div>
    </div>
  )
}
